import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Tag
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 1,
  lastSavedStep = 0,
  onStepChange,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {

  // ------------------------------ STATE
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Form values
  const [form, setForm] = useState({
    propertyType: "",
    buildingType: "",
    propertyAge: "",
    floor: "",
    totalFloors: "",
    superBuiltUpArea: "",
    builtUpAreaUnit: "",
    furnishing: "",
    otherFeatures: [],
  });

  // ------------------------------ MAPPING
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      propertyType: "com_sale_property_type",
      buildingType: "com_sale_building_type",
      floor: "floor",
      totalFloors: "total_floor",
      propertyAge: "property_age",
      furnishing: "com_rent_furnishing",
      superBuiltUpArea: "com_sale_super_built_up_area",
      builtUpAreaUnit: "built_up_area_unit",
      otherFeatures: "com_rent_other_features",
    }),
    []
  );

  // ------------------------------ VALIDATION
  const validationRules = useMemo(
    () => ({
      propertyType: { required: true, message: "Property Type is required" },
      buildingType: { required: true, message: "Building Type is required" },
      propertyAge: { required: true, message: "Property Age is required" },
      floor: { required: true, message: "Floor is required" },
      totalFloors: { required: true, message: "Total Floors is required" },
      superBuiltUpArea: {
        required: true,
        message: "Super Built-up Area is required",
        custom: (v) =>
          !v || Number(v) <= 0 ? "Super Built-up Area must be greater than 0" : null,
      },
      builtUpAreaUnit: {
        required: true, 
        message: "Unit for Super Built-up Area is required"
      },
      furnishing: { required: true, message: "Furnishing is required" },
    }),
    []
  );

  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);

      let options = attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );

      // 🏠 Custom Sorting for BHK / RK
      if (fieldKey === "bhkType") {
        options = [...options].sort((a, b) => {
          const parse = (label) => {
            const numMatch = label?.match(/\d+/);
            const number = numMatch ? Number(numMatch[0]) : 999;
            const isRK = label?.toLowerCase().includes("rk");
            const typePriority = isRK ? 0 : 1;
            return { number, typePriority };
          };

          const A = parse(a.label);
          const B = parse(b.label);

          if (A.number !== B.number) {
            return A.number - B.number;
          }

          return A.typePriority - B.typePriority;
        });
      }

      if (fieldKey === "propertyAge") {
        const orderMap = {
          "less than": 1,
          "1 to 3": 2,
          "3 to 5": 3,
          "5 to 10": 4,
          "more than": 5,
        };

        options = [...options].sort((a, b) => {
          const getPriority = (label) => {
            const lower = label?.toLowerCase() || "";
            if (lower.includes("less than")) return orderMap["less than"];
            if (lower.includes("1 to 3")) return orderMap["1 to 3"];
            if (lower.includes("3 to 5")) return orderMap["3 to 5"];
            if (lower.includes("5 to 10")) return orderMap["5 to 10"];
            if (lower.includes("more than")) return orderMap["more than"];
            return 999;
          };
          return getPriority(a.label) - getPriority(b.label);
        });
      }
      return options;
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;

      const val = form[field];

      if (rule.required && !val) return rule.message;
      if (rule.custom) return rule.custom(val);
      return null;
    },
    [form, validationRules]
  );

  const handleBlur = useCallback((f) => setTouched((p) => ({ ...p, [f]: true })), []);

  // Update field
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const toggleFeature = useCallback((featureId) => {
  setForm((prev) => {
    const current = Array.isArray(prev.otherFeatures)
      ? prev.otherFeatures
      : [];
    const exists = current.some((id) => String(id) === String(featureId));
    const next = exists
      ? current.filter((id) => String(id) !== String(featureId))
      : [...current, featureId];
    return { ...prev, otherFeatures: next };
  });
  setTouched((prev) => ({ ...prev, otherFeatures: true }));
}, []);
// ------------------------------ HYDRATE FORM WITH EXISTING DATA
useEffect(() => {
  if (!data || !attributeValues.length || isInitialized) return;
  if (!data.attributes || Object.keys(data.attributes).length === 0) return;

  const attributes = data.attributes || {};
  const areaInfo = data.area_information || {};

  console.log("📥 Sale API attributes:", attributes);

  setForm((prevForm) => {
    const updated = { ...prevForm };

    // ------------------------------
    // NORMAL FIELDS
    // ------------------------------
    updated.propertyType = attributes.com_sale_property_type ?? "";
    updated.buildingType = attributes.com_sale_building_type ?? "";
    updated.propertyAge = attributes.property_age ?? "";
    updated.floor = attributes.floor ?? "";
    updated.totalFloors = attributes.total_floor ?? "";
    updated.furnishing = attributes.com_rent_furnishing ?? "";
    updated.superBuiltUpArea = areaInfo.com_sale_super_built_up_area ?? "";
    updated.builtUpAreaUnit = areaInfo.built_up_area_unit ?? "";

    // ------------------------------
    // OTHER FEATURES
    // ------------------------------
    const otherFeaturesData = attributes.com_rent_other_features;
    const otherFeaturesOptions = getOptions("otherFeatures");

    console.log("🔍 Other Features raw:", otherFeaturesData);
    console.log(
      "🔍 Other Features options available:",
      otherFeaturesOptions.map((o) => ({ id: o.id, label: o.label }))
    );

    // Only hydrate otherFeatures if we actually have options loaded
    if (otherFeaturesOptions.length === 0) {
      console.warn("⚠️ Skipping otherFeatures hydration — options not loaded yet");
      return updated;
    }

    let featureValues = [];

    if (
      otherFeaturesData === undefined ||
      otherFeaturesData === null ||
      otherFeaturesData === ""
    ) {
      featureValues = [];
    } else if (Array.isArray(otherFeaturesData)) {
      featureValues = otherFeaturesData;
    } else if (typeof otherFeaturesData === "string") {
      featureValues = otherFeaturesData
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    } else if (typeof otherFeaturesData === "number") {
      featureValues = [otherFeaturesData];
    } else if (typeof otherFeaturesData === "boolean") {
      if (otherFeaturesData) {
        featureValues = otherFeaturesOptions.map((o) => o.id);
      }
    }

    console.log("🔍 Feature values (raw):", featureValues);

    updated.otherFeatures = featureValues
      .map((value) => {
        const byId = otherFeaturesOptions.find(
          (opt) => String(opt.id) === String(value)
        );
        if (byId) return byId.id;

        const byLabel = otherFeaturesOptions.find(
          (opt) => normalize(opt.label) === normalize(value)
        );
        return byLabel ? byLabel.id : null;
      })
      .filter(Boolean);

    console.log("✅ Mapped otherFeatures IDs:", updated.otherFeatures);
    console.log("✅ Sale Form after hydration:", updated);

    return updated;
  });

  setIsInitialized(true);   // ✅ Mark as done so it never re-runs
}, [data, attributeValues, isInitialized, getOptions, normalize]);
  // ------------------------------ SAVE
  const handleSave = useCallback(
    async (navigateNext = false) => {
      setTouched(
        Object.keys(form).reduce((a, k) => ({ ...a, [k]: true }), {})
      );

      const invalid = Object.keys(validationRules).some((k) => !!getFieldError(k));
      if (invalid) {
        setError("Please fill all required fields correctly");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        let uuid = propertyUuid;

        if (!uuid) {
          uuid = await createProperty(form);
          onSaveSuccess?.(uuid, form, currentStep);
        } else {
          // ✅ EDIT MODE: notify parent so title regeneration can run
          console.log('📝 Updating existing property (sale):', uuid);
          onSaveSuccess?.(uuid, form, currentStep);
        }

const attributes = {};
Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([formKey, apiKey]) => {
  if (formKey === "measurementUnit") return;

  const value = form[formKey];

  if (formKey === "otherFeatures") {
    const options = getOptions("otherFeatures");
    attributes[apiKey] = Array.isArray(value)
      ? value
          .map((id) => {
            const opt = options.find(
              (o) => String(o.id) === String(id)
            );
            return opt ? opt.label : null;
          })
          .filter(Boolean)
      : [];
    return;
  }

  // Other fields
  if (value !== "" && value !== null && value !== undefined) {
    attributes[apiKey] = isNaN(Number(value)) ? value : Number(value);
  }
});

console.log("📤 Payload attributes:", attributes);
console.log("📤 otherFeatures sent as:", attributes["com_rent_other_features"]);

        await saveSectionAttributes(uuid, attributes, form, "property_details");

        setSuccess("Property details saved successfully!");
        onComplete?.(form);

        if (navigateNext) {
          onSaved?.(uuid, form);
          onNavigateNext?.(form);
        }
      } catch (err) {
        console.error("Save failed:", err);
        setError("Failed to save property details");
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      validationRules,
      getFieldError,
      propertyUuid,
      currentStep,
      createProperty,
      onSaveSuccess,
      saveSectionAttributes,
      onComplete,
      onSaved,
      onNavigateNext,
      FIELD_TO_ATTRIBUTE_MAP,
    ]
  );

  // ------------------------------ RENDER UI
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
              <Stack gap={6}>
                {/* PROPERTY TYPE & BUILDING TYPE */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderDropdown("propertyType", "Property Type")}
                    </Column>
                    <Column lg={8}>
                      {renderDropdown("buildingType", "Building Type")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* PROPERTY AGE & FURNISHING */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderDropdown("propertyAge", "Property Age")}
                    </Column>
                    <Column lg={8}>
                      {renderDropdown("furnishing", "Furnishing")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* FLOOR & TOTAL FLOORS */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderDropdown("floor", "Floor")}
                    </Column>
                    <Column lg={8}>
                      {renderDropdown("totalFloors", "Total Floors")}
                    </Column>
                  </Grid>
                </FormGroup>

       {/* SUPER BUILT-UP AREA */}
<FormGroup>
  <Grid>
    <Column lg={8}>
      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
        <div style={{ flex: 2 }}>
          {renderNumberField(
            "superBuiltUpArea",
            "Super Built-up Area",
            "0",
            true
          )}
        </div>

        <div style={{ flex: 1 }}>
          {renderDropdown("builtUpAreaUnit", "Unit")}
        </div>
      </div>
    </Column>
  </Grid>
</FormGroup>

                {/* OTHER FEATURES */}
<FormGroup legendText="Other Features">
  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", padding: "0.25rem 0" }}>
    {getOptions("otherFeatures").map((feature) => {
      const currentFeatures = Array.isArray(form.otherFeatures)
        ? form.otherFeatures
        : [];

      const isSelected = currentFeatures.some(
        (id) => String(id) === String(feature.id)
      );

      return (
        <Tag
          key={feature.id}
          type={isSelected ? "blue" : "gray"}
          onClick={() => toggleFeature(feature.id)}
          style={{
            cursor: "pointer",
            padding: "0.5rem 1rem",
            fontSize: "0.875rem",
            border: isSelected ? "2px solid #0f62fe" : "2px solid transparent",
          }}
          title={feature.label}
        >
          {feature.label}
        </Tag>
      );
    })}
  </div>
</FormGroup>

                {/* BUTTONS */}
                {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}
                    onNext={() => handleSave(true)}
                    showSave
                    showNext
                    isSaveLoading={saving}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default PropertyDetails;